<template>
    <div>
        <section class="blog-header">
            <div class="header-inner" data-aos="fade-up">
                <span class="eyebrow-tag"> بلاگ لایوی </span>
                <h1 class="header-title">
                    آخرین <span class="gradient-text"> اخبار </span> جهان
                </h1>
                <p class="header-desc">
                    مهم‌ترین اخبار و رویدادهای جهانی را از نگاه لایوی دنبال کنید.
                </p>
            </div>
        </section>
        <section class="blog-content px-4">
            <div v-if="!loading && events.length === 0" class="empty-state" data-aos="fade-up">
                <div class="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                         stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                    </svg>
                </div>
                <h3>رویدادی یافت نشد</h3>
                <p>در حال حاضر رویدادی برای نمایش وجود ندارد.</p>
            </div>
            <div v-else class="events-grid">
                <article
                        v-for="(event, i) in events"
                        :key="event.id"
                        class="event-card"
                        data-aos="fade-up"
                        :data-aos-delay="(i % 4) * 80"
                >
                    <div class="card-image-wrap">
                        <a :href="/blog/ + event.slug">
                            <img
                                    :src="event.image || '/images/placeholder.png'"
                                    :alt="event.title"
                                    class="card-image"
                                    loading="lazy"
                            />
                        </a>
                        <div class="card-image-overlay"></div>
                        <div class="card-date-badge">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                                 stroke-linecap="round" stroke-linejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            {{ formatDate(event.date) }}
                        </div>
                    </div>
                    <div class="card-body">
                        <h3 class="card-title"> <a :href="/blog/ + event.slug"> {{ event.title }} </a> </h3>
                        <p class="card-desc">{{ truncateText(event.description, 110) }}</p>
                    </div>
                    <div class="card-footer text-center">
                        <a :href="'/map/' + event.id" class="card-link" target="_blank">
                            مشاهده روی نقشه
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                                 stroke-linecap="round" stroke-linejoin="round">
                                <path d="M19 12H5M12 5l-7 7 7 7" />
                            </svg>
                        </a>
                    </div>
                </article>
            </div>
            <div v-if="totalPages > 1" class="pagination-wrap" data-aos="fade-up">
                <nav class="pagination-nav">
                    <button
                            class="page-btn page-arrow"
                            :disabled="currentPage === 1"
                            @click="changePage(currentPage - 1)"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                             stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                    <template v-for="page in paginationRange">
                        <span v-if="page === '...'" :key="'dot-' + page" class="page-dots">...</span>
                        <button
                                v-else
                                :key="page"
                                class="page-btn"
                                :class="{ active: currentPage === page }"
                                @click="changePage(page)"
                        >
                            {{ page }}
                        </button>
                    </template>
                    <button
                            class="page-btn page-arrow"
                            :disabled="currentPage === totalPages"
                            @click="changePage(currentPage + 1)"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                             stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                </nav>
                <p class="pagination-info">
                    صفحه {{ currentPage }} از {{ totalPages }}
                </p>
            </div>
        </section>
    </div>
</template>

<script>
import AOS from "aos";
import { decryptResponse } from "@/utils/decrypt";
import { buildMeta } from "@/utils/seo";

export default {
    name: "Blog",
    metaInfo() {
        return buildMeta({
            title: "بلاگ لایوی - آخرین اخبار جهان",
            description: "مهم‌ترین اخبار و رویدادهای جهانی را از نگاه لایوی دنبال کنید.",
            path: "/blog",
            type: "website",
        });
    },
    data() {
        return {
            events: [],
            currentPage: 1,
            totalPages: 1,
            loading: false,
        };
    },
    computed: {
        paginationRange() {
            const total = this.totalPages;
            const current = this.currentPage;
            const range = [];

            if (total <= 7) {
                for (let i = 1; i <= total; i++) range.push(i);
                return range;
            }

            range.push(1);
            if (current > 3) range.push("...");

            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) range.push(i);

            if (current < total - 2) range.push("...");
            range.push(total);

            return range;
        },
    },
    async mounted() {
        await this.fetchEvents();
    },
    created() {
        AOS.init({ once: true, duration: 700, easing: "ease-out-cubic" });
    },
    methods: {
        async fetchEvents() {
            try {
                this.loading = true;
                const loader = this.$loading.show({ loader: "dots" });
                this.$http
                        .get(`${this.$serverUrl}/blog/events?page=${this.currentPage}`)
                        .then(async (response) => {
                            if (response.status === 200) {
                                let decryptedResponse = JSON.parse(
                                        decryptResponse(response.data)
                                );
                                this.events = decryptedResponse.events;
                                this.totalPages = decryptedResponse.total;
                            }
                        })
                        .catch(() => {})
                        .finally(() => {
                            loader.hide();
                            this.loading = false;
                        });
            } catch (error) {
                console.error("Error fetching events:", error);
                this.loading = false;
            }
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.fetchEvents();
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        },
        formatDate(date) {
            return new persianDate(new Date(date)).format("L");
        },
        truncateText(text, maxLength) {
            if (!text) return "";
            return text.length > maxLength
                    ? text.substring(0, maxLength) + "..."
                    : text;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "~aos/dist/aos.css";
@import "@/assets/css/tokens";

.blog-header {
    padding: 6rem 2rem 1rem;
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
}

.header-inner {
    max-width: 600px;
    margin: 0 auto;
}

.header-title {
    font-size: clamp(1.8rem, 4vw, 2.6rem);
    font-weight: 900;
    line-height: 1.3;
    margin-bottom: 0.8rem;
}

.header-desc {
    font-size: 1rem;
    color: $text-secondary;
    line-height: 1.9;
}

.blog-content {
    max-width: 1200px;
    margin: 0 auto 2rem;
}

.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    h3 {
        font-size: 1.2rem;
        font-weight: 700;
        margin: 1rem 0 0.4rem;
    }
    p {
        color: $text-muted;
        font-size: 0.9rem;
    }
}

.empty-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba($accent-blue, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    color: $text-muted;
}

.events-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 1.5rem;
}

.event-card {
    background: $bg-card;
    border: 1px solid $border-light;
    border-radius: 20px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
    &:hover {
        transform: translateY(-6px);
        border-color: rgba($accent-cyan, 0.2);
        box-shadow: 0 12px 40px rgba($accent-blue, 0.08),
        0 2px 8px rgba(0, 0, 0, 0.04);
        .card-image {
            transform: scale(1.06);
        }
        .card-link svg {
            transform: translateX(-4px);
        }
    }
}

.card-image-wrap {
    position: relative;
    overflow: hidden;
    height: 180px;
}

.card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.card-image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba($text-primary, 0.05) 100%);
    pointer-events: none;
}

.card-date-badge {
    position: absolute;
    bottom: 0.8rem;
    left: 0.8rem;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 10px;
    padding: 0.3rem 0.7rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: $text-secondary;
    direction: ltr;
    svg {
        color: $accent-blue;
        transform: translateY(-1px);
    }
}

.card-body {
    padding: 1.3rem 1.4rem 1.4rem;
    display: flex;
    flex-direction: column;
    flex: 1;
}

.card-title {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.7;
    margin: 0 0 0.6rem;
    color: $text-primary;
    text-align: justify;
}

.card-desc {
    font-size: 0.82rem;
    color: $text-secondary;
    margin: 0 0;
    text-align: justify;
    flex: 1;
}

.card-link {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    font-weight: 700;
    color: $accent-blue;
    text-decoration: none;
    transition: all 0.25s ease;
    svg {
        transition: transform 0.25s ease;
    }
    &:hover {
        color: $accent-cyan;
    }
}

.pagination-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    margin-top: 3rem;
}

.pagination-nav {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.page-btn {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    border: 1.5px solid $border-light;
    background: $bg-card;
    color: $text-secondary;
    font-family: "Vazirmatn", sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;
    &:hover:not(:disabled):not(.active) {
        border-color: rgba($accent-blue, 0.3);
        color: $accent-blue;
        background: rgba($accent-blue, 0.04);
        transform: translateY(-1px);
    }
    &.active {
        background: $accent-blue;
        color: #fff;
        border-color: transparent;
        box-shadow: 0 4px 14px rgba($accent-blue, 0.25);
        transform: scale(1.05);
    }
    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
}

.page-arrow {
    border: none;
    background: transparent;
    color: $text-muted;
    width: 38px;
    height: 38px;
    &:hover:not(:disabled) {
        color: $accent-blue;
        background: rgba($accent-blue, 0.06);
        border-radius: 10px;
    }
}

.page-dots {
    width: 30px;
    text-align: center;
    color: $text-muted;
    font-weight: 700;
    letter-spacing: 2px;
    user-select: none;
}

.pagination-info {
    font-size: 0.78rem;
    color: $text-muted;
    margin: 0;
}

@media (max-width: 768px) {
    .events-grid {
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 1rem;
    }
    .card-image-wrap {
        height: 150px;
    }
}
</style>
